import { ConnectCommand } from "@/components/hero/ConnectCommand";
import { HERO_ROTATION_MS, heroSlides, site } from "@/content/site";
import { useCarousel } from "@/hooks/useCarousel";
import { asset } from "@/lib/assets";
import { cn } from "@/lib/cn";

/** Load sequence: each block rises in slightly after the one above it. */
const STAGGER_MS = 90;

const riseDelay = (step: number) => ({ animationDelay: `${String(step * STAGGER_MS)}ms` });

export function Hero() {
  const { index, goTo } = useCarousel(heroSlides.length, HERO_ROTATION_MS);
  const slide = heroSlides[index] ?? heroSlides[0];

  return (
    <section className="relative flex flex-1 flex-col pt-20 lg:pb-16 lg:pt-28">
      {/* Mobile: the character fills the space above the copy instead of
          hiding behind it, and stays height-capped so a landscape phone
          doesn't blow it up to several screens tall. */}
      <div className="pointer-events-none absolute inset-x-0 bottom-[26%] flex justify-center overflow-hidden lg:hidden">
        <img className="max-h-[54vh] w-auto max-w-full object-contain" src={asset("images/webp/promo-avatar.webp")} alt="" fetchPriority="high" />
      </div>
      <div className="pointer-events-none absolute inset-0 bg-linear-to-t from-brand-navy via-brand-navy/85 via-40% to-transparent lg:hidden" />

      <div className="relative container mx-auto flex flex-1 flex-col px-5 lg:flex-row lg:items-center">
        {/* Centred while stacked on phones, left-aligned once the character
            moves beside it on desktop. */}
        <div className="mt-auto w-full pb-10 pt-8 text-center sm:pb-14 lg:mt-0 lg:w-3/6 lg:pb-0 lg:pt-0 lg:text-left xl:w-5/12">
          {/* Fluid type instead of breakpoint steps, and tight leading so the
              two lines read as one block. The italic line is nudged left to
              optically align its slanted stem with the roman line above. */}
          {slide && (
            <h1 className="animate-rise uppercase leading-[0.85] text-balance" style={riseDelay(0)}>
              <span className="block text-[clamp(3rem,9vw,6.25rem)] font-bold tracking-tight text-white">{slide.title}</span>
              <span className="block text-[clamp(4rem,12vw,8.5rem)] font-extrabold italic tracking-[-0.01em] text-brand-sky lg:ml-[-0.055em]">
                {slide.highlight}
              </span>
            </h1>
          )}

          <div className="animate-rise mt-8 flex justify-center lg:justify-start" style={riseDelay(1)}>
            <ConnectCommand />
          </div>

          <div className="animate-rise mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-center lg:justify-start" style={riseDelay(2)}>
            <a
              href={site.serverConnectUrl}
              target="_blank"
              rel="noreferrer"
              className={cn(
                "inline-flex items-center justify-center rounded-full bg-brand-sky px-6 py-3.5 font-roboto text-base font-bold uppercase tracking-wide sm:px-10 sm:py-4 sm:text-lg text-brand-navy xl:text-xl",
                "transition-colors duration-200 hover:bg-white",
                "focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-brand-sky",
              )}
            >
              Entrar no servidor
            </a>

            <a
              href="#premios"
              className={cn(
                "inline-flex items-center justify-center rounded-full border-2 border-white/30 px-6 py-3.5 font-roboto text-base font-bold uppercase tracking-wide sm:px-10 sm:py-4 sm:text-lg text-white xl:text-xl",
                "transition-colors duration-200 hover:border-white hover:bg-white/10",
                "focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white",
              )}
            >
              Ver prémios
            </a>
          </div>

          {heroSlides.length > 1 && (
            <div className="mt-12 flex justify-center gap-4 lg:justify-start">
              {heroSlides.map((heroSlide, slideIndex) => (
                <button
                  key={heroSlide.id}
                  type="button"
                  aria-label={`Ver ${heroSlide.title}`}
                  aria-current={slideIndex === index}
                  onClick={() => {
                    goTo(slideIndex);
                  }}
                  className={cn(
                    "h-3 rounded-full transition-all duration-300",
                    slideIndex === index ? "w-10 bg-brand-sky" : "w-3 bg-white/30 hover:bg-white/60",
                  )}
                />
              ))}
            </div>
          )}
        </div>

        <div className="z-0 hidden flex-1 lg:block">
          <img src={asset("images/webp/promo-avatar.webp")} alt="" fetchPriority="high" />
        </div>
      </div>
    </section>
  );
}
